import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CompaniesSuppliersData } from '../../store/apiSlices/companies/companiesSuppliers/companiesSuppliersApiParser';
import Dropdown from '../common/Dropdown';
import FormField from '../common/FormField';
import InputField from '../common/InputField';
import Modal from '../common/Modal';
import ModalFooter from '../common/ModalFooter';

type SupplierFormValues = {
  shortName: string;
  companyId: string;
  makers: number[];
  managers: number[];
  status: string;
};

interface EditCompaniesSuppliersModalProps {
  data: CompaniesSuppliersData;
  onClose: () => void;
  onSubmit: () => void;
  control: Control<SupplierFormValues>;
  errors?: FieldErrors<SupplierFormValues>;
  isEditMode?: boolean;
  isSubmitting?: boolean;
}

const EditCompaniesSuppliersModal: React.FC<EditCompaniesSuppliersModalProps> = ({
  data,
  onClose,
  onSubmit,
  control,
  errors,
  isEditMode = false,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={true}
      onClose={onClose}
      size="sm"
      title={isEditMode ? t('companies.editSupplier') : t('companies.addSupplier')}
      footer={
        <ModalFooter
          onCancel={onClose}
          onSubmit={onSubmit}
          isLoading={isSubmitting}
          submitText={
            isSubmitting
              ? isEditMode
                ? t('buttons.updating')
                : t('buttons.adding')
              : isEditMode
                ? t('buttons.update')
                : t('buttons.add')
          }
        />
      }
    >
      <FormField label={t('form.shortName')} sx={{ mt: 2 }} required>
        <Controller
          name="shortName"
          control={control}
          rules={{ required: t('form.validation.shortNameRequired') }}
          render={({ field }) => (
            <InputField
              {...field}
              value={field.value || ''}
              placeholder={t('form.shortNamePlaceholder')}
              error={!!errors?.shortName}
              helperText={errors?.shortName?.message as string}
            />
          )}
        />
      </FormField>

      <FormField label={t('form.company')} sx={{ mt: 2 }} required>
        <Controller
          name="companyId"
          control={control}
          rules={{ required: t('form.validation.companyRequired') }}
          render={({ field }) => (
            <Dropdown
              value={field.value ?? ''}
              onChange={(e: any) => field.onChange(e.target.value)}
              options={data.companies}
              placeholder={t('form.selectCompany')}
              disabled={isEditMode}
              debounceMs={200}
              maxDisplayedOptions={50}
              error={!!errors?.companyId}
              helperText={errors?.companyId?.message as string}
            />
          )}
        />
      </FormField>

      <FormField label={t('form.makers')} sx={{ mt: 2 }} required>
        <Controller
          name="makers"
          control={control}
          rules={{
            required: t('form.validation.makersRequired'),
            validate: (value) => {
              if (!Array.isArray(value) || value.length === 0) {
                return t('companies.atLeastOneMakerRequired');
              }
              return true;
            },
          }}
          render={({ field }) => (
            <Dropdown
              value={field.value ?? []}
              onChange={(e: any) => {
                const arr = Array.isArray(e.target.value)
                  ? e.target.value.map((v: any) => Number(v))
                  : [];
                field.onChange(arr);
              }}
              options={data.makers}
              placeholder={t('form.selectMaker')}
              multiSelect
              debounceMs={200}
              maxDisplayedOptions={50}
              error={!!errors?.makers}
              helperText={errors?.makers?.message as string}
            />
          )}
        />
      </FormField>

      <FormField label={t('form.managers')} sx={{ mt: 2 }} required>
        <Controller
          name="managers"
          control={control}
          rules={{
            required: t('form.validation.managersRequired'),
            validate: (value) => {
              if (!Array.isArray(value) || value.length === 0) {
                return t('companies.atLeastOneManagerRequired');
              }
              return true;
            },
          }}
          render={({ field }) => (
            <Dropdown
              value={field.value ?? []}
              onChange={(e: any) => {
                const arr = Array.isArray(e.target.value)
                  ? e.target.value.map((v: any) => Number(v))
                  : [];
                field.onChange(arr);
              }}
              options={data.managers}
              placeholder={t('form.selectManager')}
              multiSelect
              debounceMs={200}
              maxDisplayedOptions={50}
              error={!!errors?.managers}
              helperText={errors?.managers?.message as string}
            />
          )}
        />
      </FormField>

      <FormField label={t('form.status')} sx={{ mt: 2 }}>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value ?? 'Active'}
              onChange={(e: any) => field.onChange(e.target.value)}
              options={data.statusOptions}
              placeholder={t('common.selectStatus')}
              hasSearch={false}
            />
          )}
        />
      </FormField>
    </Modal>
  );
};

export default EditCompaniesSuppliersModal;
